/**
 * invalidateActivityListCaches
 *
 * Helper centralizado para invalidar TODAS as queries que alimentam a tela
 * `/tenant/activities/list`, o feed de atividades do CRM e os contadores/KPIs
 * relacionados. Deve ser chamado após qualquer ação de cadência
 * (completar, pular, cancelar, reagendar) ou edição direta de atividade
 * disparada de dentro de um modal de negócio, para garantir que ao retornar
 * para a listagem os dados estejam atualizados automaticamente.
 *
 * Mantém uma única fonte da verdade das chaves usadas pela listagem/feed,
 * evitando drift quando novas mutações forem adicionadas.
 *
 * @version 1.0.0
 * RELEASE: 2026-05-15 v1.0.0 - Criação do helper.
 */
import type { QueryClient } from '@tanstack/react-query';

/**
 * Lista canônica de queryKeys consumidas pela listagem de atividades,
 * pelo feed do CRM, KPIs e contadores. Use sempre este helper – não
 * duplique invalidações em outros pontos.
 */
const ACTIVITY_LIST_QUERY_KEYS: readonly string[] = [
  // Listagem paginada e KPIs da página /tenant/activities/list
  'activities-paginated',
  'activities-kpis',
  'activities-type-counts',
  'activities-list',
  // Feed de atividades do CRM (timeline geral) e seus contadores/KPIs
  'crm-activities-feed',
  'activities-feed-counts',
  'activities-balance-kpi',
  'unread-mentions-count',
  // Calendário (mesma origem de dados)
  'calendar-activities',
];

/**
 * Invalida todas as queries de atividades de listagem/feed/KPIs.
 * Não invalida queries específicas de um deal (ex.: deal-cadence-*),
 * que devem continuar sendo invalidadas pelos próprios hooks.
 */
export function invalidateActivityListCaches(queryClient: QueryClient): Promise<void> {
  return Promise.all(
    ACTIVITY_LIST_QUERY_KEYS.map((key) =>
      queryClient.invalidateQueries({ queryKey: [key] }),
    ),
  ).then(() => undefined);
}
